import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Upload, BookOpen, Lock, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { UploadModal } from './appetite/UploadModal';
import { NoteModal } from './appetite/NoteModal';
import { ChatTab } from './appetite/ChatTab';
import { DocumentsTab } from './appetite/DocumentsTab';
import { DashboardTab } from './appetite/DashboardTab';
import { AuditTab } from './appetite/AuditTab';
import { QueriesTab } from './appetite/QueriesTab';
import { MyNotesTab } from './appetite/MyNotesTab';
import { ApprovalsTab } from './appetite/ApprovalsTab';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'chat', label: 'AI Chat' },
  { id: 'mynotes', label: 'My Notes' },
  { id: 'approvals', label: 'Approvals', reviewerOnly: true },
  { id: 'documents', label: 'Documents', adminOnly: true },
  { id: 'queries', label: 'Team Queries', adminOnly: true },
  { id: 'dashboard', label: 'Dashboard', adminOnly: true },
  { id: 'audit', label: 'Audit Log', adminOnly: true },
];

const EMPTY_Q_FILTERS = { user_id: '', carrier: '', state: '', lob: '', confidence: '', query_type: '', has_answer: '', search: '', date_from: '', date_to: '' };

const AppetiteSearch = ({ user: currentUser }) => {
  const isAdmin = currentUser?.role === 'admin';
  const canUpload = isAdmin || currentUser?.can_upload === true;
  const [tab, setTab] = useState('chat');
  const [documents, setDocuments] = useState([]);
  const [filters, setFilters] = useState({ carriers: [], states: [], lobs: [] });
  const [users, setUsers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [appSettings, setAppSettings] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [chatCarrier, setChatCarrier] = useState('');
  const [chatState, setChatState] = useState('');
  const [chatLob, setChatLob] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [popularQs, setPopularQs] = useState([]);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Doc filters
  const [docCarrier, setDocCarrier] = useState('');
  const [docState, setDocState] = useState('');

  // Team Queries (admin) state
  const [queries, setQueries] = useState([]);
  const [queriesTotal, setQueriesTotal] = useState(0);
  const [queryStats, setQueryStats] = useState(null);
  const [qFilters, setQFilters] = useState({ ...EMPTY_Q_FILTERS });
  const [expandedQuery, setExpandedQuery] = useState(null);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
    'Content-Type': 'application/json',
  }), []);

  const loadDocs = useCallback(async () => {
    let url = `${API}/api/appetite/documents?`;
    if (docCarrier) url += `carrier=${encodeURIComponent(docCarrier)}&`;
    if (docState) url += `state=${docState}&`;
    const res = await fetch(url, { headers: headers() });
    if (res.ok) setDocuments((await res.json()).documents || []);
  }, [headers, docCarrier, docState]);

  const loadFilters = useCallback(async () => {
    const res = await fetch(`${API}/api/appetite/filters`, { headers: headers() });
    if (res.ok) setFilters(await res.json());
  }, [headers]);

  const loadUsers = useCallback(async () => {
    const res = await fetch(`${API}/api/users`, { headers: headers() });
    if (res.ok) setUsers((await res.json()).users || []);
  }, [headers]);

  const loadDashboard = useCallback(async () => {
    const res = await fetch(`${API}/api/appetite/dashboard`, { headers: headers() });
    if (res.ok) setDashboard(await res.json());
  }, [headers]);

  const loadAudit = useCallback(async () => {
    const res = await fetch(`${API}/api/appetite/audit?limit=100`, { headers: headers() });
    if (res.ok) setAuditLog((await res.json()).audit_log || []);
  }, [headers]);

  const loadQueries = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(qFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('limit', '100');
    const res = await fetch(`${API}/api/appetite/chat-queries?${params.toString()}`, { headers: headers() });
    if (res.ok) {
      const data = await res.json();
      setQueries(data.queries || []);
      setQueriesTotal(data.total || 0);
    }
  }, [headers, qFilters]);

  const loadQueryStats = useCallback(async () => {
    const res = await fetch(`${API}/api/appetite/chat-queries/stats?days=30`, { headers: headers() });
    if (res.ok) setQueryStats(await res.json());
  }, [headers]);

  const loadPopular = useCallback(async () => {
    const res = await fetch(`${API}/api/appetite/popular-questions`, { headers: headers() });
    if (res.ok) setPopularQs((await res.json()).questions || []);
  }, [headers]);

  const loadSettings = useCallback(async () => {
    const res = await fetch(`${API}/api/settings`, { headers: headers() });
    if (res.ok) setAppSettings(await res.json());
  }, [headers]);

  useEffect(() => { loadFilters(); loadPopular(); loadSettings(); if (isAdmin) loadUsers(); }, [loadFilters, loadPopular, loadSettings, loadUsers, isAdmin]);
  useEffect(() => { if (tab === 'documents' && isAdmin) loadDocs(); }, [tab, loadDocs, isAdmin]);
  useEffect(() => { if (tab === 'dashboard' && isAdmin) loadDashboard(); }, [tab, loadDashboard, isAdmin]);
  useEffect(() => { if (tab === 'audit' && isAdmin) loadAudit(); }, [tab, loadAudit, isAdmin]);
  useEffect(() => { if (tab === 'queries' && isAdmin) { loadQueries(); loadQueryStats(); } }, [tab, loadQueries, loadQueryStats, isAdmin]);

  // Auto-poll while any document is still being indexed
  useEffect(() => {
    if (tab !== 'documents') return;
    const pending = documents.some(d => d.indexing_status === 'processing');
    if (!pending) return;
    const t = setInterval(() => { loadDocs(); loadFilters(); }, 4000);
    return () => clearInterval(t);
  }, [tab, documents, loadDocs, loadFilters]);

  const handleChat = async () => {
    if (!question.trim()) return;
    const q = question.trim();
    setQuestion('');
    setMessages(p => [...p, { id: crypto.randomUUID(), role: 'user', content: q }]);
    setChatLoading(true);

    try {
      const res = await fetch(`${API}/api/appetite/chat`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ question: q, session_id: sessionId, carrier: chatCarrier, state: chatState, lob: chatLob }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Chat failed');
        return;
      }
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages(p => [...p, { id: crypto.randomUUID(), role: 'assistant', content: data.answer, citations: data.citations, confidence: data.confidence }]);
    } catch { toast.error('Error communicating with AI'); }
    finally { setChatLoading(false); setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); }
  };

  const newSession = () => { setMessages([]); setSessionId(''); };

  const exportChatPDF = async () => {
    if (!sessionId) return toast.error('No chat session to export');
    try {
      const res = await fetch(`${API}/api/appetite/chat/sessions/${sessionId}/pdf`, { headers: { 'Authorization': headers()['Authorization'] } });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `appetite_chat_${sessionId.slice(0, 12)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Delete this document and all its indexed data?')) return;
    await fetch(`${API}/api/appetite/documents/${id}`, { method: 'DELETE', headers: headers() });
    loadDocs(); loadFilters();
  };

  const isReviewer = isAdmin || appSettings?.can_review === true;
  const isSuperAdmin = appSettings?.is_super_admin === true;
  const pendingCount = appSettings?.pending_count || 0;

  return (
    <div className="h-full flex flex-col" data-testid="appetite-search-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#101C47] flex items-center justify-center"><BookOpen size={16} className="text-white" /></div>
          <div>
            <h1 className="text-base font-bold text-gray-900">Appetite Search</h1>
            <p className="text-[10px] text-gray-400">AI-powered underwriting guideline search</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {TABS.filter(t => (!t.adminOnly || isAdmin) && (!t.reviewerOnly || isReviewer)).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 ${tab === t.id ? 'bg-[#101C47] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
              {t.id === 'approvals' && pendingCount > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white text-[#101C47]' : 'bg-amber-400 text-white'}`} data-testid="approvals-pending-badge">{pendingCount}</span>
              )}
              {t.adminOnly && <Lock size={9} className={tab === t.id ? 'text-white/60' : 'text-gray-400'} title="Admin only" />}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setShowNoteModal(true)} className="h-8 text-xs gap-1 ml-2 border-[#101C47]/30 text-[#101C47] hover:bg-[#101C47]/5" data-testid="add-note-btn">
            <StickyNote size={12} /> Add Note
          </Button>
          {canUpload && (
            <Button size="sm" onClick={() => setShowUpload(true)} className="h-8 text-xs bg-[#101C47] gap-1" data-testid="upload-btn">
              <Upload size={12} /> Upload Guideline
            </Button>
          )}
        </div>
      </div>

      {/* Admin-only banner */}
      {isAdmin && ['documents', 'queries', 'dashboard', 'audit'].includes(tab) && (
        <div className="px-6 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[11px] text-amber-800">
          <Lock size={10} /> <span className="font-semibold">Admin area</span> — only admins can view this tab.
        </div>
      )}

      {tab === 'chat' && (
        <ChatTab
          filters={filters} popularQs={popularQs} messages={messages} question={question} setQuestion={setQuestion}
          chatLoading={chatLoading} sessionId={sessionId} chatCarrier={chatCarrier} setChatCarrier={setChatCarrier}
          chatState={chatState} setChatState={setChatState} chatLob={chatLob} setChatLob={setChatLob}
          showFilters={showFilters} setShowFilters={setShowFilters} handleChat={handleChat} newSession={newSession}
          exportChatPDF={exportChatPDF} chatEndRef={chatEndRef} chatInputRef={chatInputRef}
        />
      )}

      {tab === 'mynotes' && <MyNotesTab headers={headers} />}

      {tab === 'approvals' && isReviewer && (
        <ApprovalsTab headers={headers} isSuperAdmin={isSuperAdmin} users={users} onChanged={loadSettings} />
      )}

      {tab === 'documents' && isAdmin && (
        <DocumentsTab
          documents={documents} filters={filters} docCarrier={docCarrier} setDocCarrier={setDocCarrier}
          docState={docState} setDocState={setDocState} headers={headers} loadDocs={loadDocs}
          deleteDoc={deleteDoc} onUploadClick={() => setShowUpload(true)}
        />
      )}

      {tab === 'dashboard' && isAdmin && <DashboardTab dashboard={dashboard} />}

      {tab === 'audit' && isAdmin && <AuditTab auditLog={auditLog} loadAudit={loadAudit} />}

      {tab === 'queries' && isAdmin && (
        <QueriesTab
          queries={queries} queriesTotal={queriesTotal} queryStats={queryStats} qFilters={qFilters}
          setQFilters={setQFilters} loadQueries={loadQueries} users={users} filters={filters}
          expandedQuery={expandedQuery} setExpandedQuery={setExpandedQuery}
        />
      )}

      {showNoteModal && (
        <NoteModal headers={headers} carriers={filters.carriers} onClose={() => setShowNoteModal(false)}
          onSubmitted={() => { setShowNoteModal(false); loadSettings(); }} />
      )}

      {showUpload && canUpload && (
        <UploadModal headers={headers} users={users} isAdmin={isAdmin} onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); if (isAdmin) { loadDocs(); loadFilters(); loadDashboard(); } }} />
      )}
    </div>
  );
};

export default AppetiteSearch;
